/**
 * AnalyticsDashboard Component
 * Dashboard Analytics con grafici, heatmap, word cloud e statistiche
 * Da integrare nel Dashboard.js principale
 * 
 * OTTIMIZZATO: Usa useMemo invece di useEffect + useState per calcoli immediati
 */

import React, { useState, useMemo, useEffect } from 'react';
import { 
  BarChart3, TrendingUp, Activity, Target, Hash, Calendar,
  Clock, FileText, MessageSquare, Loader2, ChevronDown, ChevronUp,
  Eye, AlertCircle
} from 'lucide-react';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Estrae parole chiave da un testo (per word cloud)
 */
const extractKeywords = (text) => {
  if (!text) return [];
  
  // Parole da ignorare (stop words italiane comuni)
  const stopWords = new Set([
    'il', 'lo', 'la', 'i', 'gli', 'le', 'un', 'uno', 'una',
    'di', 'da', 'a', 'in', 'con', 'su', 'per', 'tra', 'fra',
    'e', 'ed', 'o', 'od', 'ma', 'se', 'come', 'che', 'chi',
    'cui', 'dove', 'quando', 'perché', 'quale', 'quanto',
    'del', 'della', 'dei', 'degli', 'delle', 'al', 'allo',
    'alla', 'ai', 'agli', 'alle', 'dal', 'dallo', 'dalla',
    'dai', 'dagli', 'dalle', 'nel', 'nello', 'nella', 'nei',
    'negli', 'nelle', 'sul', 'sullo', 'sulla', 'sui', 'sugli',
    'sulle', 'è', 'sono', 'sia', 'stato', 'stata', 'stati', 'state',
    'ha', 'hanno', 'ho', 'hai', 'questo', 'questa', 'questi', 'queste',
    'quello', 'quella', 'quelli', 'quelle', 'mio', 'mia', 'miei', 'mie',
    'tuo', 'tua', 'tuoi', 'tue', 'suo', 'sua', 'suoi', 'sue',
    'cosa', 'dove', 'come', 'quando', 'perché', 'qual',
    'essere', 'avere', 'fare', 'potere', 'dovere', 'volere',
    'più', 'molto', 'poco', 'tutto', 'tanto', 'ogni', 'altro', 'cerco', 'voglio',
    'zona', 'settore', 'esperienza', 'cerco',
    'ricerco', 'sto cercando', 'desidero trovare',
    'sono in cerca di', 'indago su', 'esploro', 'mi informo su', 'voglio',
    'desidero', 'intendo', 'vorrei', 'ambisco a', 'mi prefiggo di',
    'ho l’obiettivo di', 'miro a', 'zona', 'area', 'regione', 'territorio',
    'quartiere', 'località', 'distretto', 'settore', 'settore', 'ambito',
    'campo', 'ramo', 'categoria', 'comparto', 'disciplina', 'sfera',
    'esperienza', 'competenza', 'pratica', 'conoscenza', 'abilità',
    'vissuto', 'background', 'curriculum',
  ]);
  
  // Estrai parole (minimo 3 caratteri)
  const words = text
    .toLowerCase()
    .replace(/[^\w\sàèéìòù]/g, ' ')
    .split(/\s+/)
    .filter(word => word.length >= 3 && !stopWords.has(word));
  
  // Conta occorrenze
  const wordCount = {};
  words.forEach(word => {
    wordCount[word] = (wordCount[word] || 0) + 1;
  });
  
  // Converti in array e ordina per frequenza
  return Object.entries(wordCount)
    .map(([word, count]) => ({ word, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 30); // Top 30 parole
};

/**
 * Calcola statistiche da un array di query
 */
const calculateAnalytics = (queryHistory, documents) => {
  if (!queryHistory || queryHistory.length === 0) {
    return {
      totalQueries: 0,
      avgResponseTime: 0,
      mostUsedDocuments: [],
      queriesByDay: {},
      queriesByHour: {},
      topKeywords: [],
      queryTrend: [],
      documentsUsageCount: {}
    };
  }

  // Totale query
  const totalQueries = queryHistory.length;

  // Tempo medio di risposta (simulato - in un caso reale verrebbe dal backend)
  const avgResponseTime = 2.5; // secondi (mock)

  // Query per giorno della settimana
  const queriesByDay = { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0 };
  queryHistory.forEach(q => {
    const day = new Date(q.created_at).getDay();
    queriesByDay[day]++;
  });

  // Query per ora del giorno
  const queriesByHour = {};
  for (let h = 0; h < 24; h++) queriesByHour[h] = 0;
  queryHistory.forEach(q => {
    const hour = new Date(q.created_at).getHours();
    queriesByHour[hour]++;
  });

  // Documenti più usati
  const documentsUsageCount = {};
  queryHistory.forEach(q => {
    if (q.relevant_chunks && Array.isArray(q.relevant_chunks)) {
      q.relevant_chunks.forEach(chunk => {
        const docId = chunk.document_id;
        documentsUsageCount[docId] = (documentsUsageCount[docId] || 0) + 1;
      });
    }
  });

  const mostUsedDocuments = Object.entries(documentsUsageCount)
    .map(([docId, count]) => {
      const doc = documents.find(d => d.id === parseInt(docId));
      return {
        id: parseInt(docId),
        name: doc?.name || `Documento ${docId}`,
        count
      };
    })
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);

  // Estrai keywords da tutte le query
  const allQueryText = queryHistory.map(q => q.query_text).join(' ');
  const topKeywords = extractKeywords(allQueryText);

  // Trend query per settimana (ultime 8 settimane)
  const now = new Date();
  const queryTrend = [];
  for (let i = 7; i >= 0; i--) {
    const weekStart = new Date(now);
    weekStart.setDate(now.getDate() - (i * 7));
    weekStart.setHours(0, 0, 0, 0);
    
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 7);
    
    const weekQueries = queryHistory.filter(q => {
      const qDate = new Date(q.created_at);
      return qDate >= weekStart && qDate < weekEnd;
    });
    
    queryTrend.push({
      week: `Sett. ${i === 0 ? 'corrente' : `-${i}`}`,
      count: weekQueries.length,
      startDate: weekStart
    });
  }

  return {
    totalQueries,
    avgResponseTime,
    mostUsedDocuments,
    queriesByDay,
    queriesByHour,
    topKeywords,
    queryTrend,
    documentsUsageCount
  };
};

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

/**
 * Componente Statistica Card
 */
const StatCard = ({ icon: Icon, title, value, subtitle, color = 'blue' }) => {
  const colorClasses = {
    blue: 'from-blue-500 to-blue-600',
    green: 'from-green-500 to-green-600',
    purple: 'from-purple-500 to-purple-600',
    orange: 'from-orange-500 to-orange-600',
    red: 'from-red-500 to-red-600',
    yellow: 'from-yellow-500 to-yellow-600'
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200 hover:shadow-lg transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
          <p className="text-3xl font-bold text-gray-900">{value}</p>
          {subtitle && (
            <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
          )}
        </div>
        <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${colorClasses[color]} flex items-center justify-center shadow-md`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </div>
  );
};

/**
 * Componente Grafico a Barre (Query Trend)
 */
const TrendChart = ({ data }) => {
  if (!data || data.length === 0) return null;

  const maxCount = Math.max(...data.map(d => d.count), 1);

  return (
    <div className="space-y-2">
      {data.map((item, idx) => {
        const percentage = (item.count / maxCount) * 100;
        
        return (
          <div key={idx} className="flex items-center gap-3">
            <span className="text-xs font-medium text-gray-600 w-24 text-right">
              {item.week}
            </span>
            <div className="flex-1 relative h-8 bg-gray-100 rounded-lg overflow-hidden">
              <div
                className="absolute inset-y-0 left-0 bg-gradient-to-r from-blue-500 to-purple-500 transition-all duration-500 flex items-center justify-end pr-2"
                style={{ width: `${Math.max(percentage, 2)}%` }}
              >
                {item.count > 0 && (
                  <span className="text-xs font-semibold text-white">
                    {item.count}
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

/**
 * Componente Heatmap Temporale
 */
const HeatmapChart = ({ hourData, dayData }) => {
  const days = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'];
  const hours = Array.from({ length: 24 }, (_, i) => i);
  
  // Trova il massimo per normalizzare i colori
  const maxHourValue = Math.max(...Object.values(hourData), 1);
  const maxDayValue = Math.max(...Object.values(dayData), 1);

  const getIntensity = (value, max) => {
    const intensity = (value / max) * 100;
    if (intensity === 0) return 'bg-gray-100';
    if (intensity < 25) return 'bg-blue-200';
    if (intensity < 50) return 'bg-blue-400';
    if (intensity < 75) return 'bg-blue-600';
    return 'bg-blue-800';
  };

  return (
    <div className="space-y-6">
      {/* Heatmap per giorni della settimana */}
      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-3">Query per Giorno della Settimana</h4>
        <div className="grid grid-cols-7 gap-2">
          {days.map((day, idx) => {
            const value = dayData[idx] || 0;
            const intensityClass = getIntensity(value, maxDayValue);
            
            return (
              <div key={idx} className="text-center">
                <div 
                  className={`${intensityClass} rounded-lg p-4 mb-2 transition-colors cursor-pointer hover:ring-2 hover:ring-blue-500`}
                  title={`${day}: ${value} query`}
                >
                  <span className={`text-sm font-bold ${value === 0 ? 'text-gray-400' : 'text-white'}`}>
                    {value > 0 ? value : ''}
                  </span>
                </div>
                <span className="text-xs text-gray-600">{day}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Heatmap per ore del giorno */}
      <div>
        <h4 className="text-sm font-semibold text-gray-700 mb-3">Query per Ora del Giorno</h4>
        <div className="grid grid-cols-12 gap-2">
          {hours.map(hour => {
            const value = hourData[hour] || 0;
            const intensityClass = getIntensity(value, maxHourValue);
            
            return (
              <div key={hour} className="text-center">
                <div 
                  className={`${intensityClass} rounded p-2 mb-1 transition-colors cursor-pointer hover:ring-2 hover:ring-blue-500`}
                  title={`${hour}:00 - ${value} query`}
                >
                  <span className={`text-xs font-bold ${value === 0 ? 'text-gray-400' : 'text-white'}`}>
                    {value > 0 ? value : ''}
                  </span>
                </div>
                <span className="text-[10px] text-gray-500 mt-1 block">{hour}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

/**
 * Componente Word Cloud
 */
const WordCloud = ({ keywords }) => {
  if (!keywords || keywords.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-400">
        <div className="text-center">
          <Hash className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p className="text-sm">Nessun termine da visualizzare</p>
        </div>
      </div>
    );
  }

  // Normalizza i conteggi per il sizing
  const maxCount = Math.max(...keywords.map(k => k.count), 1);
  const minSize = 12;
  const maxSize = 48;

  return (
    <div className="flex flex-wrap gap-3 p-4 bg-gray-50 rounded-lg">
      {keywords.map((keyword, idx) => {
        // Calcola dimensione font basata sulla frequenza
        const size = minSize + ((keyword.count / maxCount) * (maxSize - minSize));
        
        // Colori casuali ma coerenti
        const colors = [
          'text-blue-600',
          'text-purple-600',
          'text-green-600',
          'text-orange-600',
          'text-pink-600',
          'text-indigo-600',
          'text-red-600',
          'text-teal-600'
        ];
        const colorClass = colors[idx % colors.length];

        return (
          <span
            key={idx}
            className={`${colorClass} font-semibold hover:scale-110 transition-transform cursor-pointer inline-block`}
            style={{ fontSize: `${size}px` }}
            title={`${keyword.word}: ${keyword.count} occorrenze`}
          >
            {keyword.word}
          </span>
        );
      })}
    </div>
  );
};

/**
 * Componente Documenti Più Consultati
 */
const TopDocuments = ({ documents, onViewDocument }) => {
  if (!documents || documents.length === 0) {
    return (
      <div className="flex items-center justify-center h-40 text-gray-400">
        <div className="text-center">
          <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p className="text-sm">Nessun documento consultato</p>
        </div>
      </div>
    );
  }

  const maxCount = Math.max(...documents.map(d => d.count), 1);

  return (
    <div className="space-y-3">
      {documents.map((doc, idx) => {
        const percentage = (doc.count / maxCount) * 100;
        const medal = idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : null;
        
        return (
          <div key={doc.id} className="relative">
            <div className="flex items-center gap-3 mb-1">
              <span className="text-lg">{medal || `${idx + 1}.`}</span>
              <button
                onClick={() => onViewDocument && onViewDocument(doc.id, doc.name)}
                className="flex-1 text-left text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline truncate"
              >
                {doc.name}
              </button>
              <span className="text-sm font-bold text-gray-700 ml-2">
                {doc.count}
              </span>
            </div>
            <div className="ml-8 h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-green-400 to-green-600 transition-all duration-500"
                style={{ width: `${percentage}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

/**
 * Analytics Dashboard Component
 * 
 * Props:
 * - queryHistory: array di query eseguite
 * - documents: array di documenti disponibili
 * - onViewDocument: callback per visualizzare un documento
 * - loading: boolean per stato caricamento
 */
const AnalyticsDashboard = ({ 
  queryHistory = [], 
  documents = [], 
  onViewDocument,
  loading = false 
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [activeSection, setActiveSection] = useState('overview');
  const [minLoadingTimeElapsed, setMinLoadingTimeElapsed] = useState(false);

  // Timer per garantire che il loader sia visibile per almeno 3 secondi
  useEffect(() => {
    setMinLoadingTimeElapsed(false);
    
    const timer = setTimeout(() => {
      setMinLoadingTimeElapsed(true);
    }, 3000);
    
    return () => clearTimeout(timer);
  }, [loading, queryHistory.length]);

  // ✅ OTTIMIZZAZIONE: Usa useMemo invece di useEffect + useState
  // Calcola analytics solo quando queryHistory o documents cambiano
  const analytics = useMemo(() => {
    if (loading || queryHistory.length === 0) return null;
    return calculateAnalytics(queryHistory, documents);
  }, [queryHistory, documents, loading]);

  // Mostra loader finché i dati sono in loading E non sono passati 3 secondi
  const shouldShowLoader = (loading || (queryHistory.length > 0 && !analytics)) && !minLoadingTimeElapsed;
  
  if (shouldShowLoader) {
      return (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center justify-center py-2">
              {/*<Loader2 className="w-8 h-8 animate-spin text-blue-600" />*/}
              <DotLottieReact
              src="https://lottie.host/19afbdae-3b96-451d-9047-f53c2b275a9e/JLETFWAOYn.lottie"
              loop
              autoplay
              style={{
                  maxWidth: "800px",
                  width: "100%",
              }}
              />
          </div>
          <div className="flex items-center justify-center py-4">
            <span className="ml-3 text-gray-400 font-bold">Caricamento analytics in corso...</span>
          </div>
        </div>
      );
  }

  const hasData = queryHistory.length > 0 && analytics !== null;

  return (
    <div className="px-0 pb-6">
        {!hasData ? (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center justify-center py-2">
              <DotLottieReact
              src="https://lottie.host/fd477e18-3614-4374-83f5-57d20896b6f7/5uXwBfKZB3.lottie"
              loop
              autoplay
              style={{
                  maxWidth: "800px",
                  width: "100%",
              }}
              />
          </div>
          <div className="flex items-center justify-center py-4">
            <span className="ml-3 text-gray-400 font-bold">Analytics al momento non disponibili</span>
          </div>
        </div>
        ) : (
        <>
            {/* Navigation Tabs */}
            <div className="flex gap-2 mb-6 bg-white rounded-lg p-1 shadow-sm">
            {[
                { id: 'overview', label: 'Panoramica', icon: Activity },
                { id: 'temporal', label: 'Analisi Temporale', icon: Clock },
                { id: 'keywords', label: 'Parole Chiave', icon: Hash },
                { id: 'documents', label: 'Documenti', icon: FileText }
            ].map(section => {
                const Icon = section.icon;
                const isActive = activeSection === section.id;
                
                return (
                <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg font-medium text-sm transition ${
                    isActive
                        ? 'bg-blue-600 text-white shadow-md'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                >
                    <Icon className="w-4 h-4" />
                    {section.label}
                </button>
                );
            })}
            </div>

            {/* Section Content */}
            <div className="bg-white rounded-lg p-6 shadow-sm">
            {/* Overview Section */}
            {activeSection === 'overview' && (
                <div className="space-y-6">
                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <StatCard
                    icon={MessageSquare}
                    title="Totale Query"
                    value={analytics.totalQueries}
                    subtitle="Query eseguite"
                    color="blue"
                    />
                    <StatCard
                    icon={Clock}
                    title="Tempo Medio"
                    value={`${analytics.avgResponseTime}s`}
                    subtitle="Risposta media"
                    color="green"
                    />
                    <StatCard
                    icon={FileText}
                    title="Documenti Maggiormente Utilizzati"
                    value={analytics.mostUsedDocuments.length}
                    subtitle="Documenti consultati"
                    color="purple"
                    />
                    <StatCard
                    icon={TrendingUp}
                    title="Query/Settimana"
                    value={Math.round(analytics.totalQueries / 8)}
                    subtitle="Media ultimi 2 mesi"
                    color="orange"
                    />
                </div>

                {/* Trend Chart */}
                <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                    <TrendingUp className="w-5 h-5 mr-2 text-blue-600" />
                    Trend Query (Ultime 8 Settimane)
                    </h3>
                    <TrendChart data={analytics.queryTrend} />
                </div>
                </div>
            )}

            {/* Temporal Analysis Section */}
            {activeSection === 'temporal' && (
                <div className="space-y-6">
                <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                    <Clock className="w-5 h-5 mr-2 text-blue-600" />
                    Distribuzione Temporale Query
                    </h3>
                    <p className="text-sm text-gray-600 mb-6">
                    Visualizzazione delle query per giorno della settimana e ora del giorno
                    </p>
                    <HeatmapChart 
                    hourData={analytics.queriesByHour}
                    dayData={analytics.queriesByDay}
                    />
                </div>

                {/* Insights */}
                <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <h4 className="font-semibold text-blue-900 mb-2 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-2" />
                    Insights
                    </h4>
                    <ul className="text-sm text-blue-800 space-y-1">
                    <li>• Giorno più attivo: {
                        ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'][
                        Object.entries(analytics.queriesByDay)
                            .sort((a, b) => b[1] - a[1])[0][0]
                        ]
                    } ({Math.max(...Object.values(analytics.queriesByDay))} query)</li>
                    <li>• Ora di picco: {
                        Object.entries(analytics.queriesByHour)
                        .sort((a, b) => b[1] - a[1])[0][0]
                    }:00 ({Math.max(...Object.values(analytics.queriesByHour))} query)</li>
                    </ul>
                </div>
                </div>
            )}

            {/* Keywords Section */}
            {activeSection === 'keywords' && (
                <div className="space-y-6">
                <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                    <Hash className="w-5 h-5 mr-2 text-blue-600" />
                    Termini Più Cercati
                    </h3>
                    <p className="text-sm text-gray-600 mb-6">
                    Word cloud dei termini più frequenti nelle query
                    </p>
                    <WordCloud keywords={analytics.topKeywords} />
                </div>

                {/* Top Keywords List */}
                {analytics.topKeywords.length > 0 && (
                    <div>
                    <h4 className="font-semibold text-gray-700 mb-3">Top 10 Termini</h4>
                    <div className="grid grid-cols-2 gap-3">
                        {analytics.topKeywords.slice(0, 10).map((kw, idx) => (
                        <div 
                            key={idx}
                            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200"
                        >
                            <span className="font-medium text-gray-800">{kw.word}</span>
                            <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
                            {kw.count}
                            </span>
                        </div>
                        ))}
                    </div>
                    </div>
                )}
                </div>
            )}

            {/* Documents Section */}
            {activeSection === 'documents' && (
                <div className="space-y-6">
                <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                    <FileText className="w-5 h-5 mr-2 text-blue-600" />
                    Documenti Più Consultati
                    </h3>
                    <p className="text-sm text-gray-600 mb-6">
                    Classifica dei documenti più utilizzati nelle query
                    </p>
                    <TopDocuments 
                    documents={analytics.mostUsedDocuments}
                    onViewDocument={onViewDocument}
                    />
                </div>

                {/* Document Stats */}
                {analytics.mostUsedDocuments.length > 0 && (
                    <div className="grid grid-cols-2 gap-4">
                    <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                        <p className="text-sm text-green-700 mb-1">Documento Più Usato</p>
                        <p className="text-lg font-bold text-green-900 truncate">
                        {analytics.mostUsedDocuments[0].name}
                        </p>
                        <p className="text-xs text-green-600 mt-1">
                        {analytics.mostUsedDocuments[0].count} riferimenti
                        </p>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                        <p className="text-sm text-purple-700 mb-1">Copertura Documenti</p>
                        <p className="text-lg font-bold text-purple-900">
                        {Math.round((analytics.mostUsedDocuments.length / documents.length) * 100)}%
                        </p>
                        <p className="text-xs text-purple-600 mt-1">
                        {analytics.mostUsedDocuments.length} di {documents.length} utilizzati
                        </p>
                    </div>
                    </div>
                )}
                </div>
            )}
            </div>
        </>
        )}
    </div>
  );
};

export default AnalyticsDashboard;